import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertOctagon, Printer, AlertTriangle, X, Eye, PauseCircle, BellPlus, ChevronDown, ChevronUp, CalendarClock, HardHat, Download } from 'lucide-react';
import { BillingDiagnosticGroups, DIAG_ORDER, DIAG_META, DiagBadge, fmtMoney, missingRevenueText, techLabel, techNamesText, techTooltip } from '../../components/BillingDiagnosticGroups';
import BillingFollowUpDialog, { FollowUpChip } from '../../components/BillingFollowUpDialog';
import { exportCsv, printRows } from '../../components/WeeklyBillingFollowUpTab';
import { InvoiceLedgerExport } from '../../components/InvoiceLedgerExport';
import BillingDetailSheet from '../../components/BillingDetailSheet';
import BillingCreateSheet from '../../components/BillingCreateSheet';
import RefreshIndicator from '../../components/RefreshIndicator';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import CustomerTabsBar from '../../components/CustomerTabsBar';
import { fmtLocalDate } from '../../lib/timeFmt';

const AdminInvoices = () => {
  const { hasPermission, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [sortMode, setSortMode] = useState('received_desc'); // iter237 — default newest received by office
  const [selected, setSelected] = useState(null);
  const [printOnOpen, setPrintOnOpen] = useState(null);
  const [editOnOpen, setEditOnOpen] = useState(false);  // iter129
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || 'all';
  // iter225 — Billing Needs Attention queue.
  const [attention, setAttention] = useState(null);
  const [showAttention, setShowAttention] = useState(false);
  const [attnGroup, setAttnGroup] = useState(null); // iter310 — null = all groups
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [followupTarget, setFollowupTarget] = useState(null);
  const openAttention = (grp = null) => { setAttnGroup(grp); setShowBreakdown(false); setShowAttention(true); };
  // iter228 — read-only recalculated-alerts audit.
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditFilter, setAuditFilter] = useState('all'); // all | legit | suppressed

  const openAudit = async () => {
    setShowAudit(true);
    try {
      const { data } = await axios.get(`${API}/admin/invoices/attention-audit`, { withCredentials: true });
      setAudit(data);
    } catch (e) { toast.error('Failed to load audit'); }
  };

  const fetchAttention = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/invoices/attention`, { withCredentials: true });
      setAttention(data);
    } catch (e) { /* non-blocking */ }
  }, []);

  // iter103 — Auto-refresh so Stripe webhook + customer-pay status changes show up
  // without a manual reload. Paused while a detail sheet is open (so the admin's edits
  // aren't clobbered mid-flight) and while typing in any input.
  const fetchInvoices = useCallback(async () => {
    const { data } = await axios.get(`${API}/invoices/all?sort=${sortMode}`, { withCredentials: true });
    setInvoices(data);
    fetchAttention();
  }, [fetchAttention, sortMode]);

  const { refresh, lastUpdated, isFetching } = useAutoRefresh(fetchInvoices, {
    intervalMs: 20000,
    // iter103 — Don't pause when the detail sheet is open. We need polling to continue
    // so the open sheet picks up Stripe webhook / customer-pay status flips. The sync
    // effect below only re-syncs the sheet on payment-relevant field changes.
    pausedByWizard: false,
  });

  useEffect(() => { refresh(); }, [refresh]);
  // iter237 — refetch with the new server-side order when the sort changes.
  useEffect(() => { fetchInvoices(); }, [sortMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter225 — deep-link from the Dashboard alert opens the attention queue.
  useEffect(() => {
    if (searchParams.get('attention') === '1') openAttention(searchParams.get('group') || null);
  }, [searchParams]);

  // 911-fix-iter172 — Deep-link support so /admin/invoices?open={id}[&edit=1]
  // opens the right invoice sheet straight away (used by the
  // JobPreviewSheet "View / Edit Invoice" buttons). Clears the param after
  // applying so a manual reload doesn't fight the user.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || invoices.length === 0) return;
    const target = invoices.find((i) => i.id === openId);
    if (target) {
      setSelected(target);
      setEditOnOpen(searchParams.get('edit') === '1');
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [invoices, searchParams, setSearchParams]);

  // Refresh the moment the browser tab regains focus (covers "paid in another tab").
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refresh();
    });
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  // iter103 — When the invoice list refreshes, re-sync the currently-open detail sheet
  // ONLY when payment-affecting fields change (status, amount_paid). This keeps the
  // Stripe webhook / customer-pay status update flowing into the sheet while preserving
  // any in-flight line-item edits the admin may be making.
  useEffect(() => {
    if (!selected) return;
    const fresh = invoices.find((i) => i.id === selected.id);
    if (!fresh) return;
    const paymentChanged =
      fresh.status !== selected.status ||
      (fresh.amount_paid || 0) !== (selected.amount_paid || 0) ||
      (fresh.paid_at || '') !== (selected.paid_at || '') ||
      // iter134 — Phase 2: also re-sync when HSP status flips (e.g. pending_office_review →
      // submitted_to_hsp via Mark Submitted to HSP) so the open sheet's badge updates inline.
      (fresh.hsp_review_status || '') !== (selected.hsp_review_status || '') ||
      ((fresh.hsp_payments || []).length) !== ((selected.hsp_payments || []).length) ||
      // iter136 — Phase 3: re-sync when office billing (QB) status changes from the row
      // dropdown or from the BillingDetailSheet dropdown so the badge + history list stay live.
      (fresh.office_billing_status || '') !== (selected.office_billing_status || '') ||
      ((fresh.office_billing_history || []).length) !== ((selected.office_billing_history || []).length) ||
      // iter310 — follow-up saved from the open sheet.
      JSON.stringify(fresh.billing_followup || null) !== JSON.stringify(selected.billing_followup || null);
    if (paymentChanged) {
      setSelected(fresh);
    }
  }, [invoices, selected]);

  const getStatusColor = (s) => ({
    paid: 'text-green-500 bg-green-500/10',
    pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
    overdue: 'text-red-500 bg-red-500/10',
    // iter105 — Refund states
    refunded: 'text-purple-300 bg-purple-500/15 border border-purple-700/40',
    partially_refunded: 'text-purple-200 bg-purple-500/10 border border-purple-700/30',
    deposit_paid: 'text-blue-300 bg-blue-500/10 border border-blue-700/30',
    void: 'text-[#525252] bg-[#262626]',
  })[s] || 'text-[#A3A3A3] bg-[#262626]';

  // iter105 — Pretty label so "partially_refunded" doesn't show as "PARTIALLY_REFUNDED" cramped.
  const formatStatus = (s) => {
    if (!s) return '';
    if (s === 'deposit_paid') return 'PARTIALLY PAID';   // iter240 — spec wording
    return s.toString().replace(/_/g, ' ').toUpperCase();
  };

  // iter175 — HSP status badge config. For warranty/HSP invoices, the
  // hsp_review_status (pending_office_review → submitted_to_hsp →
  // partially_paid → hsp_paid) represents the REAL operational status
  // even when the underlying `invoice.status` is still 'draft'. Surface it
  // inline next to the main badge so office staff don't mistake a processed
  // HSP claim for an unfinished draft.
  const HSP_BADGE_BY_STATUS = {
    pending_office_review: { label: 'PENDING REVIEW', cls: 'text-amber-200 bg-amber-500/10 border-amber-500/40' },
    submitted_to_hsp:      { label: 'SUBMITTED TO HSP', cls: 'text-blue-200 bg-blue-500/15 border-blue-500/40' },
    partially_paid:        { label: 'HSP PARTIAL',     cls: 'text-purple-200 bg-purple-500/15 border-purple-500/40' },
    hsp_paid:              { label: 'HSP PAID',        cls: 'text-green-300 bg-green-500/15 border-green-500/40' },
  };
  // iter175 — When `inv.status === 'draft'` but the operational pipeline
  // is well past draft (HSP submitted or QB entered), the loud "DRAFT"
  // chip is misleading. Mute it so the secondary badges carry the meaning.
  const isOperationallyProcessed = (inv) => {
    const hsp = inv.hsp_review_status;
    if (hsp && hsp !== 'pending_office_review') return true;
    const qb = inv.office_billing_status;
    if (qb === 'entered_in_qb' || qb === 'sent_to_hsp') return true;
    return false;
  };
  const statusChipCls = (inv) => {
    if (inv.status === 'draft' && isOperationallyProcessed(inv)) {
      // Muted: signals "this is not actually open work" without lying
      // about the underlying status field.
      return 'text-[#A3A3A3] bg-[#1a1a1a] border border-[#3a3a3a]';
    }
    return getStatusColor(inv.status);
  };

  // iter152 — Date filter (Today / This Week (Sat–Fri) / This Month / This Year / Custom).
  // Tech filter (free-text search across first/last/full name + invoice tech_name).
  const [dateRange, setDateRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [techSearch, setTechSearch] = useState('');
  // iter181 — Invoice-specific search bar. Case-insensitive across customer
  // name, phone (digits-only match), email, service+billing address, invoice
  // number, description/notes, tech name, and amount (with or without $).
  const [invSearch, setInvSearch] = useState('');

  // iter198 — /admin/invoices?customer=<name> prefills the search (used by the
  // "View Invoices" quick action on membership rows).
  useEffect(() => {
    const cust = searchParams.get('customer');
    if (cust) {
      setInvSearch(cust);
      const next = new URLSearchParams(searchParams);
      next.delete('customer');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // iter210 — FIX: all invoice date filtering now uses America/New_York calendar
  // dates. Root cause of the "Today shows $0" bug: the filter compared UTC
  // instants (new Date(i.date)) against browser-local bounds, so an invoice
  // stored at a UTC-midnight-ish stamp landed on the PRIOR Eastern calendar day
  // after ~8 PM ET — visible in This Week (wide window) but dropped by Today.
  const ET_TZ = 'America/New_York';
  // Eastern calendar date ("YYYY-MM-DD") for an instant. en-CA yields ISO order.
  const etDateStr = (d = new Date()) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: ET_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  // Local Y-M-D string (never toISOString — that would shift to UTC).
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Effective Eastern calendar date for an invoice. Prefer the service/issue
  // date (already an ET date string from the API); otherwise convert the created
  // timestamp to its Eastern calendar date. Rows, counts, and the Total Billed
  // card all use THIS single source of truth.
  const invoiceEtDate = (i) => {
    if (i.service_date) return String(i.service_date).slice(0, 10);
    if (i.date) { try { return etDateStr(new Date(i.date)); } catch { return null; } }
    return null;
  };

  // Inclusive [startStr, endStr] Eastern date-string window for the active range.
  const etDateWindow = useMemo(() => {
    const todayEt = etDateStr();
    // Noon on the ET calendar day, in local time — math only reads Y/M/D so DST
    // and the browser tz can't shift the calendar date.
    const base = new Date(`${todayEt}T12:00:00`);
    switch (dateRange) {
      case 'today':
        return [todayEt, todayEt];
      case 'this_week': {
        const a = new Date(base); a.setDate(a.getDate() - ((a.getDay() + 1) % 7)); // back to Saturday
        const b = new Date(a); b.setDate(b.getDate() + 6);                          // through Friday
        return [ymd(a), ymd(b)];
      }
      case 'this_month': {
        const a = new Date(base.getFullYear(), base.getMonth(), 1, 12);
        const b = new Date(base.getFullYear(), base.getMonth() + 1, 0, 12);         // last day of month
        return [ymd(a), ymd(b)];
      }
      case 'this_year':
        return [`${base.getFullYear()}-01-01`, `${base.getFullYear()}-12-31`];
      case 'custom': {
        if (!customFrom && !customTo) return [null, null];
        return [customFrom || '0000-01-01', customTo || '9999-12-31'];
      }
      default:
        return [null, null];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customFrom, customTo]);

  // Single date matcher shared by the rows, the status-chip counts, and the total.
  const matchesDateRange = (i) => {
    const [from, to] = etDateWindow;
    if (!from || !to) return true;
    const d = invoiceEtDate(i);
    return !!d && d >= from && d <= to;
  };

  const filtered = useMemo(() => {
    let out = status === 'all' ? invoices : invoices.filter((i) => i.status === status);
    out = out.filter(matchesDateRange);
    const q = techSearch.trim().toLowerCase();
    if (q) {
      out = out.filter((i) => (i.tech_name || '').toLowerCase().includes(q));
    }
    // iter181 — Invoice-specific search across many fields.
    const raw = invSearch.trim();
    if (raw) {
      const lower = raw.toLowerCase();
      const digits = raw.replace(/\D/g, '');
      const amountLike = raw.replace(/[$,\s]/g, '');
      const amountNum = Number(amountLike);
      const isAmount = amountLike !== '' && !Number.isNaN(amountNum);
      out = out.filter((i) => {
        const bag = [
          i.customer_name, i.customer_phone, i.customer_email,
          i.homeowner_name, i.job_for_name,
          i.service_address, i.billing_address,
          i.invoice_number, i.description, i.notes,
          i.quickbooks_invoice_number,
          i.tech_name,
        ].map((v) => (v == null ? '' : String(v).toLowerCase())).join(' | ');
        if (bag.includes(lower)) return true;
        // Phone digits-only match (handles "(740) 919-1599" vs "7409191599")
        if (digits && digits.length >= 3) {
          const phoneDigits = String(i.customer_phone || '').replace(/\D/g, '');
          if (phoneDigits.includes(digits)) return true;
        }
        // Amount match: exact numeric match OR integer-dollar prefix. Prevents
        // "50" from matching "$250.00" while still letting "1800" match "$1800.00"
        // (via exact match) and "$50" match "$50.00".
        if (isAmount) {
          const amt = Number(i.amount) || 0;
          if (Math.abs(amt - amountNum) < 0.005) return true;
          if (amt.toFixed(2).startsWith(amountLike)) return true;
        }
        return false;
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, status, etDateWindow, techSearch, invSearch]);

  // iter152 — Build the unique tech list (active field techs) from the loaded invoices
  // so the dropdown reflects actual attribution. Office-only employees naturally won't
  // appear unless they have invoices attributed to them — which matches user spec.
  const uniqueTechs = useMemo(() => {
    const seen = new Map();
    invoices.forEach((i) => {
      const name = (i.tech_name || '').trim();
      if (!name || name === 'Unassigned') return;
      if (!seen.has(name)) seen.set(name, (i.tech_id || name));
    });
    return Array.from(seen.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [invoices]);

  // iter136 — Phase 3: Office Billing (QuickBooks) Status helpers.
  const OFFICE_BILLING_OPTIONS = [
    { key: 'needs_review',  label: 'Needs Review',   color: 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30' },
    { key: 'ready_for_qb',  label: 'Ready for QB',   color: 'text-blue-300 bg-blue-500/10 border-blue-500/30' },
    { key: 'entered_in_qb', label: 'Entered in QB',  color: 'text-green-400 bg-green-500/10 border-green-500/30' },
    { key: 'paid',          label: 'Paid',           color: 'text-green-300 bg-green-500/15 border-green-500/40' },
    { key: 'on_hold',       label: 'On Hold',        color: 'text-orange-300 bg-orange-500/10 border-orange-500/30' },
  ];
  const OFFICE_BILLING_ROLES_FE = new Set(['super_admin', 'admin', 'service_manager', 'office_staff', 'manager']);
  const canManageOfficeBilling = OFFICE_BILLING_ROLES_FE.has(user?.role);
  const officeBillingLabel = (k) => (OFFICE_BILLING_OPTIONS.find((o) => o.key === k)?.label) || 'Needs Review';
  const officeBillingColor = (k) => (OFFICE_BILLING_OPTIONS.find((o) => o.key === k)?.color) || OFFICE_BILLING_OPTIONS[0].color;

  const handleQuickQbStatusChange = async (invId, nextStatus, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API}/invoices/${invId}/office-status`,
        { office_billing_status: nextStatus }, { withCredentials: true });
      toast.success(`QB Status → ${officeBillingLabel(nextStatus)}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update QB status');
    }
  };

  const total = filtered.reduce((acc, i) => acc + (i.amount || 0), 0);

  // iter225 — direct actions from the attention queue.
  const reviewInvoice = (invId) => {
    const inv = invoices.find((i) => i.id === invId);
    setShowAttention(false);
    if (inv) setSelected(inv);
  };
  const holdInvoice = async (invId) => {
    const note = window.prompt('Reason for putting this invoice on hold (required):');
    if (note == null) return;
    if (!note.trim()) { toast.error('A reason is required.'); return; }
    try {
      await axios.post(`${API}/invoices/${invId}/office-status`, { office_billing_status: 'on_hold', note: note.trim() }, { withCredentials: true });
      toast.success('Invoice put on hold');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter229 — actions for completed jobs with no invoice.
  const reviewJob = () => { setShowAttention(false); window.location.href = '/admin/billing-missing'; };
  const createMissingDraft = async (row) => {
    if (!row.completion_id) { toast.error('No completion form on this job — review it first.'); return; }
    const reason = window.prompt('Reason for creating the missing invoice draft (required):');
    if (reason == null) return;
    if (reason.trim().length < 5) { toast.error('A reason of at least 5 characters is required.'); return; }
    try {
      await axios.post(`${API}/admin/completions/${row.completion_id}/create-office-review-draft`, { amount_basis: 'office_to_determine', reason: reason.trim() }, { withCredentials: true });
      toast.success('Missing invoice draft created for office review');
      fetchAttention(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create draft'); }
  };
  const linkExistingInvoice = async (row) => {
    const num = window.prompt('Enter the existing invoice number to link to this job:');
    if (num == null) return;
    const match = invoices.find((i) => (i.invoice_number || '').toLowerCase() === num.trim().toLowerCase());
    if (!match) { toast.error(`No invoice found with number "${num}".`); return; }
    const reason = window.prompt(`Link invoice ${match.invoice_number} to ${row.customer_name}? Reason (required):`);
    if (reason == null) return;
    if (reason.trim().length < 5) { toast.error('A reason of at least 5 characters is required.'); return; }
    try {
      await axios.post(`${API}/admin/completed-jobs/link-invoice`, { invoice_id: match.id, appointment_id: row.appointment_id, completion_id: row.completion_id, reason: reason.trim() }, { withCredentials: true });
      toast.success(`Linked invoice ${match.invoice_number} to the job`);
      fetchAttention(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link invoice'); }
  };
  const markNoInvoiceValid = async (row) => {
    const reason = window.prompt('Why is it valid for this completed job to have NO invoice? (required):');
    if (reason == null) return;
    if (reason.trim().length < 5) { toast.error('A reason of at least 5 characters is required.'); return; }
    try {
      await axios.post(`${API}/admin/unfinished-billing/${row.appointment_id}/override`, { reason: reason.trim() }, { withCredentials: true });
      toast.success('Marked as a valid no-invoice outcome');
      fetchAttention(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const setStatus = (s) => {
    const p = new URLSearchParams(searchParams);
    if (s === 'all') p.delete('status'); else p.set('status', s);
    setSearchParams(p);
  };

  return (
    <div data-testid="admin-invoices-page" className="space-y-4">
      <CustomerTabsBar userRole={user?.role} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">All Invoices</h2>
          <p className="text-[#A3A3A3]">Company-wide invoice overview</p>
          <RefreshIndicator
            lastUpdated={lastUpdated}
            isFetching={isFetching}
            refresh={refresh}
            testId="admin-invoices-refresh"
          />
          {/* 911-fix-iter172 — Quick jump to Billing Diagnostics so the office
              can immediately find invoices that didn't make it through. */}
          <Link
            to="/admin/billing-diagnostics"
            data-testid="open-billing-diagnostics"
            className="inline-flex items-center gap-1 text-red-300 hover:text-red-200 text-xs font-bold mt-2"
          >
            <AlertOctagon className="w-3.5 h-3.5" /> Billing Diagnostics — find missing/orphan invoices
          </Link>
        </div>
        {hasPermission('create_invoices') && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* iter104 — One-shot reconcile for invoices that Stripe charged but the old
                `_finalize_invoice_payment` source-guard bug left stuck on PENDING in the DB. */}
            <button
              type="button"
              data-testid="reconcile-stripe-btn"
              onClick={async () => {
                if (!window.confirm('Re-check Stripe for any pending invoice payments from the last 90 days and mark them paid where Stripe confirms the payment? This is safe and idempotent.')) return;
                try {
                  const { data } = await axios.post(`${API}/admin/invoices/reconcile-pending-stripe`, {}, { withCredentials: true });
                  const msg = `Checked ${data.checked} pending. ${data.paid_now} marked paid${data.refunds_detected ? `, ${data.refunds_detected} refund(s) detected` : ''}.${data.still_pending ? ` ${data.still_pending} still pending in Stripe.` : ''}${data.other ? ` ${data.other} stale/expired.` : ''}`;
                  if (data.paid_now > 0 || data.refunds_detected > 0) toast.success(msg); else toast.message(msg);
                  refresh();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Reconcile failed');
                }
              }}
              className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              title="Re-check Stripe for any payments that didn't sync to invoices"
            >
              Reconcile Stripe Payments
            </button>
            <BillingCreateSheet
              type="invoice"
              onCreated={(newDoc) => {
                refresh();
                if (newDoc) { setSelected(newDoc); setEditOnOpen(true); }
              }}
            />
          </div>
        )}
      </div>

      {/* iter310 — Billing Diagnostics banner: 5 operational groups, $ = Potential Missing Revenue only. */}
      {attention && (attention.total_needing_action ?? attention.count) > 0 && (
        <div
          role="button"
          tabIndex={0}
          data-testid="billing-attention-banner"
          onClick={() => openAttention(null)}
          className={`w-full text-left rounded-md border p-4 flex items-start gap-3 transition-colors cursor-pointer ${attention.at_risk_count > 0 ? 'border-red-500/60 bg-red-500/10 hover:bg-red-500/15' : 'border-orange-500/50 bg-orange-500/10 hover:bg-orange-500/15'}`}
        >
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${attention.at_risk_count > 0 ? 'text-red-400' : 'text-orange-400'}`} />
          <div className="flex-1 min-w-0 space-y-2">
            <p className={`font-bold ${attention.at_risk_count > 0 ? 'text-red-300' : 'text-orange-300'}`}>
              {(attention.total_needing_action ?? attention.count)} billing item{(attention.total_needing_action ?? attention.count) === 1 ? '' : 's'} need attention
              {attention.at_risk_count > 0 && <> — <span className="text-white" data-testid="billing-banner-missing-revenue">{missingRevenueText(attention.missing_revenue)} potential missing revenue</span></>}
            </p>
            <BillingDiagnosticGroups groups={attention.groups} onSelect={openAttention} testidPrefix="banner-group" />
            {attention.followups?.open > 0 && (
              <p className="text-[11px] text-[#A3A3A3]" data-testid="banner-followups">
                <CalendarClock className="w-3 h-3 inline mr-1" />{attention.followups.open} open follow-up(s){attention.followups.overdue ? <> · <span className="text-red-300 font-bold">{attention.followups.overdue} overdue</span></> : ''}{attention.followups.due_this_week ? ` · ${attention.followups.due_this_week} due this week` : ''}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="text-xs font-bold uppercase tracking-wider text-white">Open →</span>
            <Link data-testid="banner-weekly-followup-link" to="/admin/billing-diagnostics?tab=followup" onClick={(e) => e.stopPropagation()} className="text-[11px] text-[#D4AF37] hover:underline">Weekly Follow-Up</Link>
          </div>
        </div>
      )}

      {/* iter152 — Date + Tech filters. Update Total Billed, invoice count, and the
          status chip tallies together. Sat–Fri week per NexFlow business calendar. */}
      <div data-testid="invoice-advanced-filters" className="flex flex-wrap items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
        <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold mr-1">Range</span>
        {[
          { key: 'all',        label: 'All' },
          { key: 'today',      label: 'Today' },
          { key: 'this_week',  label: 'This Week' },
          { key: 'this_month', label: 'This Month' },
          { key: 'this_year',  label: 'This Year' },
          { key: 'custom',     label: 'Custom…' },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`invoice-date-${f.key}`}
            onClick={() => setDateRange(f.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
              dateRange === f.key ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]' : 'bg-[#141414] border-[#262626] text-[#A3A3A3] hover:text-white'
            }`}
          >{f.label}</button>
        ))}
        {dateRange === 'custom' && (
          <div className="flex items-center gap-1.5">
            <input data-testid="invoice-date-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-7 px-2 text-xs text-white" />
            <span className="text-[#525252] text-xs">→</span>
            <input data-testid="invoice-date-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-7 px-2 text-xs text-white" />
          </div>
        )}
        <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold ml-3 mr-1">Tech</span>
        <input
          data-testid="invoice-tech-search"
          type="text"
          list="invoice-tech-options"
          value={techSearch}
          onChange={(e) => setTechSearch(e.target.value)}
          placeholder="All Techs"
          className="bg-[#141414] border border-[#262626] rounded h-7 px-2 text-xs text-white w-44"
        />
        <datalist id="invoice-tech-options">
          {uniqueTechs.map(([name]) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        {techSearch && (
          <button onClick={() => setTechSearch('')} className="text-[10px] text-[#A3A3A3] hover:text-white underline">Clear</button>
        )}

        {/* iter181 — Invoice-specific search bar (customer, phone, address, amount, invoice #, description, tech). */}
        <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold ml-3 mr-1">Search</span>
        <input
          data-testid="invoice-search"
          type="search"
          value={invSearch}
          onChange={(e) => setInvSearch(e.target.value)}
          placeholder="Search invoices by customer, phone, address, amount, invoice #..."
          className="bg-[#141414] border border-[#262626] rounded h-7 px-2 text-xs text-white w-72"
        />
        {invSearch && (
          <button
            data-testid="invoice-search-clear"
            onClick={() => setInvSearch('')}
            className="text-[10px] text-[#A3A3A3] hover:text-white underline"
          >
            Clear
          </button>
        )}

        {/* iter237 — Intake ordering. Default: newest received by office first. */}
        <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold ml-3 mr-1">Sort</span>
        <select
          data-testid="invoice-sort"
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          className="bg-[#141414] border border-[#262626] rounded h-7 px-2 text-xs text-white"
        >
          <option value="received_desc">Newest received</option>
          <option value="received_asc">Oldest received</option>
          <option value="service_desc">Newest service date</option>
          <option value="service_asc">Oldest service date</option>
        </select>
      </div>

      <div className="bg-[#141414] border border-[#D4AF37] rounded-md p-6">
        <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">
          Total Billed
          {status !== 'all' && ` — ${status.toUpperCase()} only`}
          {dateRange !== 'all' && ` · ${dateRange.replace(/_/g, ' ')}`}
          {techSearch && ` · ${techSearch}`}
          {invSearch && ` · "${invSearch}"`}
        </p>
        <p data-testid="invoice-total-billed" className="text-[#D4AF37] text-4xl font-bold mt-2">${total.toFixed(2)}</p>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-1">
          <p className="text-white text-sm">{filtered.length} invoice(s)</p>
          <InvoiceLedgerExport currentIds={filtered.map((i) => i.id)} sort={sortMode}
            viewLabel={[status !== 'all' && status.toUpperCase(), dateRange !== 'all' && (dateRange === 'custom' ? `${customFrom || '…'} to ${customTo || '…'}` : dateRange.replace(/_/g, ' ')), techSearch && `Tech: ${techSearch}`, invSearch && `Search: "${invSearch}"`].filter(Boolean).join(' · ')} />
        </div>
      </div>

      {/* Status filter chips */}
      <div data-testid="invoice-status-filter" className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'all',     label: 'All' },
          { key: 'pending', label: 'Pending' },
          { key: 'paid',    label: 'Paid' },
          { key: 'overdue', label: 'Overdue' },
        ].map((f) => {
          // iter210 — chip counts share the same ET date matcher as the rows.
          const tq = techSearch.trim().toLowerCase();
          const count = invoices.filter((i) => {
            if (f.key !== 'all' && i.status !== f.key) return false;
            if (!matchesDateRange(i)) return false;
            if (tq && !(i.tech_name || '').toLowerCase().includes(tq)) return false;
            return true;
          }).length;
          return (
            <button
              key={f.key}
              data-testid={`invoice-filter-${f.key}`}
              onClick={() => setStatus(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                status === f.key
                  ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                  : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#3f3f3f] hover:text-white'
              }`}
            >
              {f.label} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0A0A0A] border-b border-[#262626]">
            <tr>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">Invoice #</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">Customer</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">Tech</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">Description</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">Date</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">Status</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-sm">QB Status</th>
              <th className="text-right px-6 py-4 text-[#A3A3A3] text-sm">Amount</th>
              <th className="text-right px-6 py-4 text-[#A3A3A3] text-sm">Print</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center px-6 py-8 text-[#A3A3A3]">No invoices match the current filters</td></tr>
            ) : filtered.map((inv) => (
              <tr key={inv.id} data-testid={`admin-invoice-row-${inv.id}`} onClick={() => setSelected(inv)} className="border-b border-[#262626] last:border-0 hover:bg-[#1A1A1A] cursor-pointer">
                <td className="px-6 py-4 text-white font-medium">
                  {inv.invoice_number}
                  {inv.payor_kind === 'warranty' && (
                    <span data-testid={`warranty-badge-${inv.id}`} className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40">Warranty</span>
                  )}
                  {inv.new_warranty_company_pending && (
                    <span data-testid={`new-warranty-co-badge-${inv.id}`} className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-900/30 text-red-400 border border-red-700/40 animate-pulse">New Warranty Co</span>
                  )}
                  {inv.quickbooks_invoice_number && (
                    <span data-testid={`qb-invoice-number-${inv.id}`} className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md bg-[#1E3A2F] border border-[#2E7D5B]/60 text-[#5FD69B] text-xs font-semibold whitespace-nowrap">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-[#3FA877]">QB</span>
                      #{inv.quickbooks_invoice_number}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-white">
                  {inv.customer_name}
                  {/* iter296 — PM invoices: show Account / Service Location / Requester
                      so the office distinguishes CHN 601 King St from CHN 17 Hawkers Ave. */}
                  {inv.is_pm_invoice && (
                    <div data-testid={`inv-pm-context-${inv.id}`} className="mt-0.5 space-y-0.5">
                      {inv.pm_company_name && <div className="text-[10px] text-[#D4AF37]">{inv.pm_company_name}</div>}
                      {(inv.property_name || inv.service_address) && (
                        <div className="text-[10px] text-cyan-300">📍 {inv.property_name || inv.service_address}</div>
                      )}
                      {inv.pm_requester_name && (
                        <div className="text-[10px] text-[#A3A3A3]">Requested by: {inv.requester_state === 'unknown' ? 'Unknown' : inv.pm_requester_name}</div>
                      )}
                      {inv.pm_billing_profile_name && (
                        <div className="text-[10px] text-[#A3A3A3]">Bill to: {inv.pm_billing_profile_name}</div>
                      )}
                    </div>
                  )}
                  {/* iter134/iter152 — show real homeowner on warranty rows so payroll/dispatch
                      can read it without opening the sheet. job_for_name is the iter152
                      canonical key; homeowner_name stays as fallback for older rows. */}
                  {inv.payor_kind === 'warranty' && (inv.job_for_name || inv.homeowner_name) && (
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5">Job for: {inv.job_for_name || inv.homeowner_name}</div>
                  )}
                </td>
                {/* iter152 — Tech column. Backend already resolved attribution
                    completion → claimed → assigned. Unattributed rows show a soft dash. */}
                <td className="px-6 py-4 text-sm" data-testid={`invoice-tech-${inv.id}`}>
                  {inv.tech_name && inv.tech_name !== 'Unassigned' ? (
                    <span className="text-white">{inv.tech_name}</span>
                  ) : (
                    <span className="text-[#525252] italic">Unassigned</span>
                  )}
                </td>
                <td className="px-6 py-4 text-[#A3A3A3] truncate max-w-xs">{inv.notes || inv.description || inv.line_items?.[0]?.description || ''}</td>
                <td className="px-6 py-4 text-sm" data-testid={`invoice-dates-${inv.id}`}>
                  <div className="flex flex-col leading-tight">
                    <span className="text-white text-xs">
                      <span className="text-[#525252]">Received:</span>{' '}
                      {inv.office_received_at_effective
                        ? new Date(inv.office_received_at_effective).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : '—'}
                      {inv.office_received_is_fallback && <span className="text-[#525252]" title="Estimated from creation date"> ~</span>}
                    </span>
                    <span className="text-[#A3A3A3] text-xs">
                      <span className="text-[#525252]">Service:</span> {fmtLocalDate(inv.service_date || inv.date)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span data-testid={`invoice-status-${inv.id}`} className={`px-2 py-1 rounded text-xs font-bold ${statusChipCls(inv)}`}>
                      {formatStatus(inv.status)}
                    </span>
                    {/* iter228 — Hub delivery badges only when Hub sending is enabled. */}
                    {inv.hub_sending_enabled && inv.delivery_status_view === 'not_sent' && !inv.not_sent_intentional && (
                      <span data-testid={`invoice-notsent-${inv.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-orange-300 bg-orange-500/10 border border-orange-500/40">Not Sent</span>
                    )}
                    {inv.at_risk && (
                      <span data-testid={`invoice-at-risk-${inv.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-500/50 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Missing Revenue</span>
                    )}
                    {/* iter175 — Surface HSP operational status next to DRAFT/PAID so HSP
                        invoices that are already submitted or paid don't look unfinished. */}
                    {inv.hsp_review_status && HSP_BADGE_BY_STATUS[inv.hsp_review_status] && (
                      <span
                        data-testid={`hsp-status-${inv.id}`}
                        title={`HSP claim packet: ${HSP_BADGE_BY_STATUS[inv.hsp_review_status].label}`}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${HSP_BADGE_BY_STATUS[inv.hsp_review_status].cls}`}
                      >
                        {HSP_BADGE_BY_STATUS[inv.hsp_review_status].label}
                      </span>
                    )}
                    {/* iter165 — Payment-method chip next to PAID/HSP_PAID so office staff see at a glance how it was paid.
                        iter196 — When `payment_method` is 'other' AND we have a detail (Zelle/QB Pay/Already Paid),
                        show the detail label instead of just OTHER so office reconciliation is unambiguous. */}
                    {(inv.status === 'paid' || inv.status === 'hsp_paid') && inv.payment_method && (
                      <span
                        data-testid={`invoice-pm-${inv.id}`}
                        title={`Paid via ${inv.payment_method_detail || inv.payment_method}`}
                        className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-green-400 bg-green-500/10 border border-green-500/30"
                      >
                        {(() => {
                          if (inv.payment_method === 'other' && inv.payment_method_detail) {
                            const slug = String(inv.payment_method_detail).toLowerCase().replace(/[\s./-]+/g, '_');
                            if (['quickbooks_pay', 'quickbooks_pay_gopayment'].includes(slug)) return 'QB PAY';
                            if (slug === 'zelle') return 'ZELLE';
                            if (slug === 'already_paid' || slug === 'alreadypaid') return 'ALREADY PAID';
                            return String(inv.payment_method_detail).toUpperCase();
                          }
                          const map = { cash: 'CASH', check: 'CHECK', card_in_person: 'CARD', stripe: 'CARD', stripe_webhook: 'CARD', stripe_public_link: 'CARD', ach: 'ACH', hsp: 'HSP', warranty: 'WARRANTY', other: 'OTHER' };
                          return map[inv.payment_method] || String(inv.payment_method).replace(/_/g, ' ').toUpperCase();
                        })()}
                      </span>
                    )}
                    {/* iter196 — Payment-notes indicator so office sees "tech left a note" on the list row without opening the drawer. */}
                    {inv.payment_notes && (
                      <span
                        data-testid={`invoice-pm-notes-${inv.id}`}
                        title={inv.payment_notes}
                        className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40"
                      >
                        NOTES
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {canManageOfficeBilling ? (
                    <select
                      data-testid={`qb-status-${inv.id}`}
                      value={inv.office_billing_status || 'needs_review'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleQuickQbStatusChange(inv.id, e.target.value, e)}
                      className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded border bg-transparent cursor-pointer ${officeBillingColor(inv.office_billing_status)}`}
                    >
                      {OFFICE_BILLING_OPTIONS.map((o) => (
                        <option key={o.key} value={o.key} className="bg-[#141414] text-white">{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span data-testid={`qb-status-readonly-${inv.id}`} className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${officeBillingColor(inv.office_billing_status)}`}>
                      {officeBillingLabel(inv.office_billing_status)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-right text-[#D4AF37] font-bold">${inv.amount?.toFixed(2)}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    data-testid={`invoice-print-${inv.id}`}
                    title="Print customer invoice"
                    onClick={(e) => { e.stopPropagation(); setPrintOnOpen('customer'); setSelected(inv); }}
                    className="p-1.5 rounded hover:bg-[#262626] text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BillingDetailSheet
        open={!!selected}
        onOpenChange={(o) => { if (!o) { setSelected(null); setEditOnOpen(false); setPrintOnOpen(null); } }}
        doc={selected}
        type="invoice"
        viewerRole="admin"
        editOnOpen={editOnOpen}
        printOnOpen={printOnOpen}
        onChange={refresh}
      />

      {/* iter310 — Billing Needs Attention popup: 5 clickable groups + "what makes up $X" breakdown + follow-ups. */}
      {showAttention && attention && (() => {
        const inGroup = (cats) => !attnGroup || (cats || []).includes(attnGroup);
        const invRows = attention.items.filter((r) => inGroup(r.diagnostic_categories) && (r.diagnostic_categories || []).length > 0);
        const jobRows = (attention.no_invoice_jobs || []).filter((r) => inGroup(r.diagnostic_categories));
        const sections = attnGroup ? [attnGroup] : DIAG_ORDER;
        const mr = attention.missing_revenue || {};
        const openFollowUp = (recordType, id, followup, label) => setFollowupTarget({ recordType, id, followup, label });
        // iter313 — Export / Print exactly what the popup currently shows (active group filter).
        const viewRows = [
          ...invRows.map((r) => ({ ...r, record_type: 'invoice', qb_status: r.qb_status_view, categories: r.diagnostic_categories, followup: r.billing_followup })),
          ...jobRows.map((r) => ({ ...r, record_type: 'job', id: r.appointment_id, amount: r.missing_revenue_amount, qb_status: null, categories: r.diagnostic_categories || [], followup: r.billing_followup })),
        ];
        const viewLabel = attnGroup ? DIAG_META[attnGroup].label : 'All';
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="billing-attention-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowAttention(false)} />
          <div className="relative bg-[#141414] border border-[#262626] rounded-lg w-full max-w-4xl max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4 space-y-3 z-10">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-white font-['Outfit'] text-xl font-bold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Billing Needs Attention</h3>
                  <p className="text-[#A3A3A3] text-xs mt-1">
                    {attention.total_needing_action ?? attention.count} item(s) · {attention.count} invoice(s)
                    {attention.no_invoice_jobs_count > 0 && <> · {attention.no_invoice_jobs_count} completed job(s) with no invoice</>}
                    {mr.count > 0 && <> · <span className="text-red-300 font-bold" data-testid="attention-missing-revenue-total">{missingRevenueText(mr)} potential missing revenue</span></>}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link data-testid="attention-weekly-followup-link" to="/admin/billing-diagnostics?tab=followup" className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Weekly Follow-Up</Link>
                  <button data-testid="attention-export-csv" disabled={!viewRows.length} onClick={() => exportCsv(viewRows, `needs-attention-${attnGroup || 'all'}`)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 inline-flex items-center gap-1 disabled:opacity-40"><Download className="w-3 h-3" /> Export CSV</button>
                  <button data-testid="attention-print" disabled={!viewRows.length} onClick={() => printRows(viewRows, `Billing Needs Attention — ${viewLabel}`)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 disabled:opacity-40"><Printer className="w-3 h-3" /> Print</button>
                  <button data-testid="attention-audit-open" onClick={openAudit} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white">View Audit</button>
                  <button data-testid="attention-close" onClick={() => setShowAttention(false)} className="text-[#A3A3A3] hover:text-white p-1.5 rounded hover:bg-[#262626]"><X className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="attention-group-filter-all" onClick={() => setAttnGroup(null)} className={`px-2.5 py-1 rounded-full border text-xs font-bold ${!attnGroup ? 'border-white text-white bg-white/10' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>All</button>
                <BillingDiagnosticGroups groups={attention.groups} onSelect={(g) => setAttnGroup(g)} active={attnGroup} testidPrefix="attention-group-filter" />
              </div>
            </div>
            <div className="p-3 space-y-4">
              {/* Read-only "what makes up $X" breakdown — every record that counts toward Missing Revenue. */}
              {mr.count > 0 && (!attnGroup || attnGroup === 'missing_revenue') && (
                <div className="rounded-md border border-red-500/40 bg-red-500/5" data-testid="missing-revenue-breakdown">
                  <button data-testid="missing-revenue-breakdown-toggle" onClick={() => setShowBreakdown((v) => !v)} className="w-full flex items-center justify-between p-3 text-left">
                    <span className="text-red-300 font-bold text-sm">Show what makes up {missingRevenueText(mr)}</span>
                    {showBreakdown ? <ChevronUp className="w-4 h-4 text-red-300" /> : <ChevronDown className="w-4 h-4 text-red-300" />}
                  </button>
                  {showBreakdown && (
                    <div className="px-3 pb-3">
                      <p className="text-[11px] text-[#A3A3A3] mb-2">Only real unbilled / uncollected money counts here. Normal invoices waiting for QuickBooks entry are bookkeeping and are NOT included. Amounts are never guessed — jobs without a trustworthy field total show "$ unknown".</p>
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3]"><tr>{['Customer', 'Service Date', 'Tech', 'Invoice / Job', 'Why it counts', 'Amount'].map((h) => <th key={h} className="text-left py-1 pr-2">{h}</th>)}</tr></thead>
                        <tbody>
                          {mr.breakdown.map((b) => (
                            <tr key={`${b.record_type}-${b.id}`} data-testid={`breakdown-row-${b.id}`} className="border-t border-[#262626] align-top">
                              <td className="py-1.5 pr-2 text-white">{b.customer_name || 'Unknown'}</td>
                              <td className="py-1.5 pr-2 text-[#D4D4D4] whitespace-nowrap">{b.service_date || '—'}</td>
                              <td className="py-1.5 pr-2 text-[#D4D4D4]" title={techTooltip(b)} data-testid={`breakdown-tech-${b.id}`}>{techNamesText(b)}</td>
                              <td className="py-1.5 pr-2 font-mono text-[#A3A3A3] whitespace-nowrap">{b.record_type === 'invoice' ? (b.invoice_number || `…${b.id.slice(-6)}`) : (b.work_order_number ? `WO ${b.work_order_number}` : 'Job — no invoice')}</td>
                              <td className="py-1.5 pr-2 text-orange-300">{b.reason}</td>
                              <td className="py-1.5 text-right font-bold whitespace-nowrap" data-testid={`breakdown-amount-${b.id}`}>{b.amount == null ? <span className="text-[#7f7f7f] font-normal">$ unknown</span> : <span className="text-red-300">{fmtMoney(b.amount)}</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {invRows.length === 0 && jobRows.length === 0 ? (
                <p className="text-[#A3A3A3] text-center py-8" data-testid="attention-empty">Nothing in this group.</p>
              ) : (<>
                {sections.map((grp) => {
                const rows = invRows.filter((r) => (attnGroup ? true : r.diagnostic_categories[0] === grp));
                if (rows.length === 0) return null;
                return (
                  <div key={grp} data-testid={`attention-group-${grp}`}>
                    <p className="text-white font-bold text-sm mb-1.5 flex items-center gap-2">
                      {DIAG_META[grp].label}
                      <span className="text-[#525252] text-xs font-normal">({rows.length} invoice{rows.length === 1 ? '' : 's'})</span>
                    </p>
                    <div className="space-y-2">
                      {rows.map((r) => (
                        <div key={r.id} data-testid={`attention-row-${r.id}`} className={`rounded-md border p-3 ${r.at_risk ? 'border-red-500/50 bg-red-500/5' : 'border-[#262626] bg-[#0A0A0A]'}`}>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                                {r.customer_name || 'Unknown customer'} <span className="text-[#D4AF37] font-bold">{fmtMoney(r.amount)}</span>
                                <span className="text-[#525252] text-xs font-mono">{r.invoice_number}</span>
                                {r.at_risk && <span data-testid={`attention-missing-revenue-${r.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-500/50">Missing Revenue · {fmtMoney(r.missing_revenue_amount)}</span>}
                              </p>
                              <p className="text-xs text-[#A3A3A3] mt-0.5">
                                {r.service_date || '—'} · {r.days_pending ?? 0} day(s) waiting
                              </p>
                              <p data-testid={`attention-tech-${r.id}`} title={techTooltip(r)} className="text-sm text-white font-semibold mt-1 inline-flex items-center gap-1.5"><HardHat className="w-3.5 h-3.5 text-[#D4AF37]" />{techLabel(r)}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {r.diagnostic_categories.map((c) => <DiagBadge key={c} cat={c} />)}
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]">{r.payment_status_view}</span>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]">QB: {r.qb_status_view.replace(/_/g, ' ')}</span>
                                <FollowUpChip followup={r.billing_followup} testid={`attention-followup-chip-${r.id}`} onClick={() => openFollowUp('invoice', r.id, r.billing_followup, `${r.customer_name || 'Unknown'} · ${r.invoice_number || ''}`)} />
                              </div>
                              {r.missing_revenue_reason && <p className="text-[11px] text-red-300 mt-1.5">Why it counts: {r.missing_revenue_reason}</p>}
                              <p className="text-[11px] text-[#7f7f7f] mt-1">{r.workflow_issues.map((i) => i.label).join(' · ')}</p>
                              <p className="text-[11px] text-orange-300 mt-1" data-testid={`attention-action-${r.id}`}>Action required: {r.recommended_action}</p>
                            </div>
                            <div className="flex flex-col gap-1.5 shrink-0">
                              <button data-testid={`attention-review-${r.id}`} onClick={() => reviewInvoice(r.id)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Review Invoice</button>
                              <button data-testid={`attention-followup-${r.id}`} onClick={() => openFollowUp('invoice', r.id, r.billing_followup, `${r.customer_name || 'Unknown'} · ${r.invoice_number || ''}`)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"><BellPlus className="w-3 h-3" /> {r.billing_followup && !r.billing_followup.resolved ? 'Edit Follow-Up' : 'Add Follow-Up'}</button>
                              <button data-testid={`attention-hold-${r.id}`} onClick={() => holdInvoice(r.id)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"><PauseCircle className="w-3 h-3" /> Hold</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
                })}

                {/* Completed billable jobs with NO invoice (from appointments + completions). */}
                {jobRows.length > 0 && (
                  <div data-testid="attention-group-no_invoice_jobs">
                    <p className="text-white font-bold text-sm mb-1.5 flex items-center gap-2">
                      Completed Job — No Invoice Created
                      <span className="text-[#525252] text-xs font-normal">({jobRows.length})</span>
                    </p>
                    <div className="space-y-2">
                      {jobRows.map((r) => (
                        <div key={r.appointment_id} data-testid={`noinvoice-row-${r.appointment_id}`} className="rounded-md border border-orange-500/40 bg-orange-500/5 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                                {r.customer_name}
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-orange-300 bg-orange-500/15 border border-orange-500/50">No Invoice</span>
                                <span className="text-xs font-bold" data-testid={`noinvoice-amount-${r.appointment_id}`}>{r.missing_revenue_amount == null ? <span className="text-[#7f7f7f] font-normal">$ unknown</span> : <span className="text-red-300">{fmtMoney(r.missing_revenue_amount)}</span>}</span>
                              </p>
                              <p className="text-xs text-[#A3A3A3] mt-0.5">
                                {r.service_date || '—'}
                                {r.work_order_number ? <> · WO {r.work_order_number}</> : ''}
                                · {r.days_waiting ?? 0} day(s) waiting
                              </p>
                              <p data-testid={`noinvoice-tech-${r.appointment_id}`} title={techTooltip(r)} className="text-sm text-white font-semibold mt-1 inline-flex items-center gap-1.5"><HardHat className="w-3.5 h-3.5 text-[#D4AF37]" />{techLabel(r)}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {(r.diagnostic_categories || []).map((c) => <DiagBadge key={c} cat={c} />)}
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]">Outcome: {r.billing_outcome_label}</span>
                                {r.completion_submitted_at && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]">Completed {fmtLocalDate(r.completion_submitted_at)}</span>
                                )}
                                <FollowUpChip followup={r.billing_followup} testid={`noinvoice-followup-chip-${r.appointment_id}`} onClick={() => openFollowUp('job', r.appointment_id, r.billing_followup, `${r.customer_name} · ${r.service_date || ''}`)} />
                              </div>
                              {r.work_performed && <p className="text-[11px] text-[#A3A3A3] mt-1 line-clamp-2">Work: {r.work_performed}</p>}
                              <p className="text-[11px] text-orange-300 mt-1.5" data-testid={`noinvoice-reason-${r.appointment_id}`}>Why no invoice: {r.no_invoice_reason}</p>
                            </div>
                            <div className="flex flex-col gap-1.5 shrink-0">
                              <button data-testid={`noinvoice-review-${r.appointment_id}`} onClick={reviewJob} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Review Job</button>
                              <button data-testid={`noinvoice-followup-${r.appointment_id}`} onClick={() => openFollowUp('job', r.appointment_id, r.billing_followup, `${r.customer_name} · ${r.service_date || ''}`)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 inline-flex items-center gap-1"><BellPlus className="w-3 h-3" /> {r.billing_followup && !r.billing_followup.resolved ? 'Edit Follow-Up' : 'Add Follow-Up'}</button>
                              <button data-testid={`noinvoice-createdraft-${r.appointment_id}`} disabled={!r.has_completion} onClick={() => createMissingDraft(r)} className={`text-[11px] font-bold px-2.5 py-1 rounded border inline-flex items-center gap-1 ${r.has_completion ? 'border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10' : 'border-[#262626] text-[#525252] cursor-not-allowed'}`}>Create Missing Draft</button>
                              <button data-testid={`noinvoice-link-${r.appointment_id}`} onClick={() => linkExistingInvoice(r)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1">Link Existing Invoice</button>
                              <button data-testid={`noinvoice-novalid-${r.appointment_id}`} onClick={() => markNoInvoiceValid(r)} className="text-[11px] font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1">Mark Valid No-Invoice</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>)}
            </div>
          </div>
        </div>
        );
      })()}

      <BillingFollowUpDialog
        open={!!followupTarget}
        onOpenChange={(o) => { if (!o) setFollowupTarget(null); }}
        recordType={followupTarget?.recordType}
        recordId={followupTarget?.id}
        followup={followupTarget?.followup}
        title={followupTarget?.label}
        onSaved={() => fetchAttention()}
      />

      {/* iter228 — Read-only recalculated-alerts audit. */}
      {showAudit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="billing-audit-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowAudit(false)} />
          <div className="relative bg-[#141414] border border-[#262626] rounded-lg w-full max-w-5xl max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4 flex items-start justify-between gap-3 z-10">
              <div>
                <h3 className="text-white font-['Outfit'] text-xl font-bold">Alerts Audit — Recalculated for QuickBooks Workflow</h3>
                {audit && (
                  <p className="text-[#A3A3A3] text-xs mt-1">
                    Old rules flagged <span className="text-white font-bold">{audit.legacy_alert_count}</span> · legitimate today <span className="text-white font-bold">{audit.current_alert_count}</span> · suppressed <span className="text-white font-bold">{audit.suppressed_alert_count}</span> · Hub sending {audit.hub_sending_enabled ? 'ENABLED' : 'DISABLED'} (read-only — nothing is changed)
                  </p>
                )}
              </div>
              <button data-testid="audit-close" onClick={() => setShowAudit(false)} className="text-[#A3A3A3] hover:text-white p-1.5 rounded hover:bg-[#262626]"><X className="w-4 h-4" /></button>
            </div>
            {!audit ? (
              <p className="text-[#A3A3A3] text-center py-10">Loading audit…</p>
            ) : (
              <>
                <div className="px-4 pt-3 flex gap-2">
                  {[['all', 'All'], ['legit', 'Legitimate today'], ['suppressed', 'Suppressed']].map(([k, lbl]) => (
                    <button key={k} data-testid={`audit-filter-${k}`} onClick={() => setAuditFilter(k)} className={`text-xs font-bold px-2.5 py-1 rounded border ${auditFilter === k ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{lbl}</button>
                  ))}
                </div>
                <div className="p-3 space-y-2">
                  {audit.rows
                    .filter((r) => auditFilter === 'all' || (auditFilter === 'legit' ? r.legitimate : r.suppressed_alerts.length > 0))
                    .map((r) => (
                      <div key={r.id} data-testid={`audit-row-${r.id}`} className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3">
                        <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                          {r.customer_name || 'Unknown'} <span className="text-[#D4AF37] font-bold">${r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          <span className="text-[#525252] text-xs font-mono">{r.invoice_number}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${r.legitimate ? 'text-red-300 border-red-500/50 bg-red-500/10' : 'text-green-300 border-green-500/50 bg-green-500/10'}`}>{r.legitimate ? 'Legitimate' : 'Not an alert now'}</span>
                        </p>
                        <p className="text-xs text-[#A3A3A3] mt-0.5">{r.service_date || '—'} · <span data-testid={`audit-tech-${r.id}`} title={techTooltip(r)} className="text-white font-semibold">{techLabel(r)}</span> · {r.payment_status} · QB: {(r.qb_status || '').replace(/_/g, ' ')}</p>
                        {r.current_alert_reasons.length > 0 && (
                          <p className="text-[11px] text-orange-300 mt-1">Current: {r.current_alert_reasons.join('; ')}</p>
                        )}
                        {r.suppressed_reasons.length > 0 && (
                          <p className="text-[11px] text-[#7f7f7f] mt-1">Suppressed: {r.suppressed_reasons.join(' ')}</p>
                        )}
                        <p className="text-[11px] text-[#A3A3A3] mt-1">Recommended: <span className="text-white">{r.recommended_action}</span></p>
                      </div>
                    ))}
                  {audit.rows.filter((r) => auditFilter === 'all' || (auditFilter === 'legit' ? r.legitimate : r.suppressed_alerts.length > 0)).length === 0 && (
                    <p className="text-[#A3A3A3] text-center py-8">No invoices in this view.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
